import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PageBreadcrumb from "../../../../shared/ui/common/PageBreadCrumb";
import ComponentCard from "../../../../shared/ui/common/ComponentCard";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import Label from "../../../../shared/ui/form/Label";
import Button from "../../../../shared/ui/button/Button";
import { useTherapist } from "../../../../context/TherapistProvider";
import { useNavigate } from "react-router";
import TherapeuticPlanList from "../../../../modules/therapeutic_plan/ui/components/TherapeuticPlanList";
import { useSubscription } from "../../../subscription";
import SubscriptionRestrictedState from "../../../../shared/ui/common/SubscriptionRestrictedState";
import { loadPlanCaseload } from "../../application/planCaseload";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import { User, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import TabSwitcher from "../../../../shared/ui/tabs/TabSwitcher";
import { useMotionSafe } from "../../../../hooks/useMotionSafe";
import { useSessionPreferences } from "../../../center";

const PANEL_ID = "therapeutic-plans-panel";

export default function TherapeuticPlans() {
  const { t } = useTranslation();
  const { reduced } = useMotionSafe();
  const { therapist } = useTherapist();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<{ patientName?: string; planType?: string }>({
    patientName: "",
  });

  const [activeTab, setActiveTab] = useState<'individual' | 'couple'>('individual');
  const [patients, setPatients] = useState<{ value: string; label: string; type: 'individual' | 'couple' }[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  useEffect(() => {
    if (!therapist?.id) return;
    setLoadingPatients(true);

    loadPlanCaseload(therapist.id)
      .then(({ patients: indList, couples: coupleList }) => {
        const indData = indList || [];
        const individualMapped = indData.map((p: { first_name: string, last_name: string }) => ({
          value: [p.first_name, p.last_name].filter(Boolean).join(" "),
          label: [p.first_name, p.last_name].filter(Boolean).join(" "),
          type: 'individual' as const,
        }));

        const coupleData = coupleList || [];
        const coupleMapped = coupleData.map((c: { display_name: string }) => ({
          value: c.display_name,
          label: c.display_name,
          type: 'couple' as const,
        }));

        setPatients([...individualMapped, ...coupleMapped]);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingPatients(false));
  }, [therapist?.id]);

  // Plans for couples only where the centre runs couples' sessions.
  const { modes } = useSessionPreferences();
  const tabs = [
    { id: "individual" as const, label: t("patient.individualTab"), icon: User },
    { id: "couple" as const, label: t("patient.coupleTab"), icon: Users },
  ].filter((tab) => tab.id === "individual" || modes.couple);
  useEffect(() => {
    if (!modes.couple && activeTab === "couple") setActiveTab("individual");
  }, [modes.couple, activeTab]);

  const filteredPatients = patients.filter((p) => p.type === activeTab);

  const handleClearFilters = () => {
    setFilters({ patientName: "" });
  };

  return (
    <>
      <PageMeta />
      <PageBreadcrumb pageTitle={t("therapeuticPlan.pageTitle")} />

      <div className="space-y-6">
        <TabSwitcher
          tabs={tabs}
          activeTab={activeTab}
          onChange={(id) => {
            setActiveTab(id);
            setFilters({ patientName: "" });
          }}
          label={t("patient.typeTabsLabel")}
          layoutId="activeTabTherapeuticPlans"
          panelId={PANEL_ID}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            id={PANEL_ID}
            role="tabpanel"
            aria-label={activeTab === "individual" ? t("patient.individualTab") : t("patient.coupleTab")}
            initial={{ opacity: 0, y: reduced ? 0 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduced ? 0 : -6 }}
            transition={{ duration: reduced ? 0.12 : 0.2 }}
            className="space-y-6"
          >
            <ComponentCard
              title={
                <div className="flex justify-between items-center w-full">
                  <span>{activeTab === "individual" ? t("patient.individualTab") : t("patient.coupleTab")}</span>
                  <Button onClick={() => navigate("/therapeutic-plans/new")}>
                    {t("therapeuticPlan.newPlan")}
                  </Button>
                </div>
              }
            >
              {isPremium ? (
                <>
                  {/* 🔹 Filtros */}
                  <div className="flex flex-col xl:flex-row xl:items-end flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[180px]">
                      <Label htmlFor="patientName">{t("therapeuticPlan.filterPatient")}</Label>
                      <FilterSelect
                        options={filteredPatients}
                        value={filters.patientName || ""}
                        onChange={(val) => setFilters(prev => ({ ...prev, patientName: val as string }))}
                        placeholder={loadingPatients ? t("common.loading") : t("therapeuticPlan.searchPlaceholder")}
                        searchPlaceholder={t("patient.searchPlaceholder")}
                        className="!rounded-lg text-sm shadow-theme-xs border-gray-300 dark:border-gray-700 bg-transparent"
                      />
                    </div>

                    {/* 🧹 Clear button */}
                    <div className="flex gap-3 pt-2 sm:pt-0">
                      <Button variant="outline" onClick={handleClearFilters}>
                        {t("common.clear")}
                      </Button>
                    </div>
                  </div>

                  <TherapeuticPlanList
                    therapistID={therapist?.id ?? ""}
                    filters={{ ...filters, planType: activeTab }}
                  />
                </>
              ) : (
                <SubscriptionRestrictedState
                  title={t("therapeuticPlan.restricted.title", "Therapeutic Plans are a Plus feature")}
                  description={t("therapeuticPlan.restricted.description", "Unlock the ability to create and manage comprehensive therapeutic plans for your patients by upgrading to Alchely Plus.")}
                />
              )}

            </ComponentCard>
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  );
}
